import { lower } from './search-text'

// SearchEntry.cs: one searchable thing. The C# masks are 64-bit; JS bitwise
// operators are 32-bit, so each mask is two 32-bit halves with the same bits:
//   char mask   a-z -> bits 0-25, 0-9 -> bits 26-35 (here: letters, digits)
//   bigram mask hash 0-63 (here: lo = 0-31, hi = 32-63)
// The masks are prechecks only: they never change a result, only skip work.

export const PATH_SEPARATOR = ' › '

export const charMask = lowerText => {
  let letters = 0
  let digits = 0
  if (lowerText) {
    for (let i = 0; i < lowerText.length; i++) {
      const c = lowerText.charCodeAt(i)
      if (c >= 97 && c <= 122) letters |= 1 << (c - 97)
      else if (c >= 48 && c <= 57) digits |= 1 << (c - 48)
    }
  }
  return { letters, digits }
}

// (token & ~target) == 0: every token char is present in the target.
export const maskWithin = (token, target) => (token.letters & ~target.letters) === 0 && (token.digits & ~target.digits) === 0

export const maskOr = (a, b) => ({ letters: a.letters | b.letters, digits: a.digits | b.digits })

export const bigramMask = lowerText => {
  let lo = 0
  let hi = 0
  if (lowerText && lowerText.length >= 2) {
    for (let i = 0; i < lowerText.length - 1; i++) {
      const a = lowerText.charCodeAt(i)
      const b = lowerText.charCodeAt(i + 1)
      if (a === 32 || b === 32) continue
      const hash = ((a * 31) ^ b) & 0x3f
      if (hash < 32) lo |= 1 << hash
      else hi |= 1 << (hash - 32)
    }
  }
  return { lo, hi }
}

// `kind` is 'course' or 'folder' (anything else gets no kind boost and no
// type chip). `updatedAt` is epoch milliseconds, or null for none (C#'s
// default DateTime: no freshness bonus, sorts as the oldest).
// `pathIds` are the ancestors' ids, root first, beside `pathLabels` (C#'s
// PathIds): the result's location links to each folder.
export const makeEntry = ({ id, name, pathLabels, pathIds = [], kind, updatedAt = null, isVideo = false, node = null }) => {
  const displayName = name || ''
  const labels = Array.isArray(pathLabels) ? pathLabels : []
  const pathDisplay = labels.length === 0 ? '' : labels.join(PATH_SEPARATOR)
  const lowerName = lower(displayName)
  const lowerPath = lower(pathDisplay)
  const nameBigrams = bigramMask(lowerName)
  const pathBigrams = bigramMask(lowerPath)
  return {
    id,
    displayName,
    lowerName,
    pathDisplay,
    lowerPath,
    pathLabels: labels,
    pathIds: Array.isArray(pathIds) ? pathIds : [],
    kind,
    isVideo: !!isVideo,
    updatedAt: typeof updatedAt === 'number' && isFinite(updatedAt) ? updatedAt : null,
    bigrams: { lo: nameBigrams.lo | pathBigrams.lo, hi: nameBigrams.hi | pathBigrams.hi },
    nameMask: charMask(lowerName),
    pathMask: charMask(lowerPath),
    node
  }
}
